import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStandouts = () => {
  const [standouts, setStandouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStandout, setEditingStandout] = useState(null);
  const [formData, setFormData] = useState({
    event_name: '',
    event_date: new Date().toISOString().split('T')[0],
    player_name: '',
    photo_url: '',
    metric: '',
    value: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchStandouts();
  }, []);

  const fetchStandouts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/standouts`);
      setStandouts(response.data);
    } catch (error) {
      console.error('Error fetching standouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let photoUrl = formData.photo_url;
      
      if (selectedFile) {
        setUploadingImage(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        const uploadResponse = await axios.post(`${API}/upload`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        photoUrl = uploadResponse.data.url;
        setUploadingImage(false);
      }
      
      const standoutData = {
        ...formData,
        photo_url: photoUrl,
        event_date: new Date(formData.event_date).toISOString()
      };
      
      if (editingStandout) {
        await axios.put(`${API}/standouts/${editingStandout.id}`, standoutData);
      } else {
        await axios.post(`${API}/standouts`, standoutData);
      }
      fetchStandouts();
      resetForm();
    } catch (error) {
      console.error('Error saving standout:', error);
      alert('Error saving standout. Please try again.');
      setUploadingImage(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this standout?')) {
      try {
        await axios.delete(`${API}/standouts/${id}`);
        fetchStandouts();
      } catch (error) {
        console.error('Error deleting standout:', error);
        alert('Error deleting standout. Please try again.');
      }
    }
  };

  const handleEdit = (standout) => {
    setEditingStandout(standout);
    setFormData({
      event_name: standout.event_name,
      event_date: new Date(standout.event_date).toISOString().split('T')[0],
      player_name: standout.player_name,
      photo_url: standout.photo_url,
      metric: standout.metric,
      value: standout.value
    });
    setImagePreview(standout.photo_url);
    setSelectedFile(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingStandout(null);
    setFormData({
      event_name: '',
      event_date: new Date().toISOString().split('T')[0],
      player_name: '',
      photo_url: '',
      metric: '',
      value: ''
    });
    setSelectedFile(null);
    setImagePreview(null);
    setUploadingImage(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={20} /> Back to Dashboard
              </Link>
              <h1 className="display-large">Manage Standouts</h1>
            </div>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> Add Standout
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {showForm && (
            <div className="network-card" style={{ marginBottom: '32px' }}>
              <h2 className="heading-2">{editingStandout ? 'Edit Standout' : 'Add New Standout'}</h2>
              <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div className="form-group">
                    <label>Event Name *</label>
                    <input type="text" value={formData.event_name} onChange={(e) => setFormData({...formData, event_name: e.target.value})} required placeholder="e.g., Perfect Game Spring Championship" />
                  </div>
                  <div className="form-group">
                    <label>Event Date *</label>
                    <input type="date" value={formData.event_date} onChange={(e) => setFormData({...formData, event_date: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Player Name *</label>
                    <input type="text" value={formData.player_name} onChange={(e) => setFormData({...formData, player_name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Metric *</label>
                    <input type="text" value={formData.metric} onChange={(e) => setFormData({...formData, metric: e.target.value})} required placeholder="e.g., Fastest Exit Velocity" />
                  </div>
                  <div className="form-group">
                    <label>Value *</label>
                    <input type="text" value={formData.value} onChange={(e) => setFormData({...formData, value: e.target.value})} required placeholder="e.g., 95 mph" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Player Photo *</label>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} style={{ marginBottom: '16px' }} />
                    {imagePreview && <div style={{ marginTop: '16px' }}><img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '12px', objectFit: 'cover', border: '2px solid var(--border-medium)' }} /></div>}
                    <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '8px' }}>Accepted formats: JPG, PNG, WebP (Max 5MB)</p>
                  </div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '16px' }}>
                  <button type="submit" className="btn-primary" disabled={uploadingImage}>{uploadingImage ? 'Uploading...' : (editingStandout ? 'Update Standout' : 'Add Standout')}</button>
                  <button type="button" onClick={resetForm} className="btn-secondary" disabled={uploadingImage}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <p>Loading standouts...</p>
          ) : (
            <div className="network-card">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-medium)' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Player</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Event</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Metric</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Value</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standouts.length === 0 ? (
                      <tr><td colSpan="6" style={{ padding: '24px', textAlign: 'center' }}>No standouts found</td></tr>
                    ) : (
                      standouts.map(standout => (
                        <tr key={standout.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px' }}>{standout.player_name}</td>
                          <td style={{ padding: '12px' }}>{standout.event_name}</td>
                          <td style={{ padding: '12px' }}>{new Date(standout.event_date).toLocaleDateString()}</td>
                          <td style={{ padding: '12px' }}>{standout.metric}</td>
                          <td style={{ padding: '12px' }}>{standout.value}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => handleEdit(standout)} style={{ padding: '6px 12px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Edit size={16} /> Edit
                              </button>
                              <button onClick={() => handleDelete(standout.id)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Trash2 size={16} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminStandouts;